import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KeyRound } from "lucide-react";

import { createAccessToken } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { useZodForm, z, nonEmpty } from "@/lib/validation";
import { useToast } from "@/components/ui/toast";
import { CopyButton } from "@/components/Page";
import { CreatePage, SingleFooter, Section } from "@/components/wizard";

const schema = z.object({ name: nonEmpty("Name").max(64, "Keep it under 64 characters") });
type Values = z.infer<typeof schema>;

export function CreateAccessToken() {
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const backTo = `/agents/${projectId}/settings/access`;
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useZodForm(schema, { defaultValues: { name: "" } });

  async function onSubmit(values: Values) {
    try {
      const res = await createAccessToken(projectId, values.name);
      setCreated({ name: res.access_token.name, token: res.token });
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't create the token"));
    }
  }

  // Success state: the plaintext token is shown ONCE, in place of the form.
  if (created) {
    return (
      <CreatePage
        title="Access token created"
        description="Copy it now — this is the only time it's shown."
        backTo={backTo}
        footer={<Button className="ml-auto" onClick={() => navigate(backTo)}>Done</Button>}
      >
        <Section title={created.name} description="Store this somewhere safe. You won't be able to see it again.">
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs">
            <span className="min-w-0 flex-1 break-all">{created.token}</span>
            <CopyButton value={created.token} />
          </div>
        </Section>
      </CreatePage>
    );
  }

  return (
    <CreatePage
      title="New access token"
      description="A named token for connecting a machine to this agent's /mcp endpoint."
      backTo={backTo}
      footer={
        <SingleFooter
          onCancel={() => navigate(backTo)}
          onPrimary={() => void handleSubmit(onSubmit)()}
          submitting={isSubmitting}
          submitLabel="Create token"
        />
      }
    >
      <Section title="Token details" description="Give it a name you'll recognize — e.g. the machine or CI job that uses it.">
        <Field label="Name" htmlFor="at-name" error={errors.name?.message}>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Input id="at-name" autoFocus placeholder="ci-deploy-bot" aria-invalid={!!errors.name} {...register("name")} />
          </div>
        </Field>
      </Section>
    </CreatePage>
  );
}
